import Badge from "../components/badges/Badges";
import Button from "../components/button/Button";
import { CheckBox } from "../components/checkBox/CheckBox";
import { GridCol } from "../components/table/MessageInfoTableList";
import userQuery from "../firestore/User.query";
import { MessageAttendanceList, Messages } from "../model/Message";
import { ReplyButton } from "../model/ReplyButton";
import { ReadMessages } from "../model/Users";

export class MessagingDeliveryDestinationController {

  /** 配信先・返信の集計結果 */
  replyResults = (
    replyData: [{ count: string, reply_data: string }],
    replyButtonNames: string[],
    setReplyButtonCount: React.Dispatch<React.SetStateAction<Map<string, number>>>,
    setUnreplyNum: React.Dispatch<React.SetStateAction<number>>
  ) => {
    // ボタン集計
    const replyButtonCount = new Map();
    replyButtonNames.forEach((name) => {
      const count = parseInt(replyData.find((rep) => rep.reply_data === name)?.count ?? '0');
      replyButtonCount.set(name, count);
    });
    setReplyButtonCount(replyButtonCount);

    // 未回答数
    const unreplyNum = parseInt(replyData.find((rep) => rep.reply_data === '未回答')?.count ?? '0');
    setUnreplyNum(unreplyNum);
  }

  /** 出欠の集計結果 */
  attendanceResults = (
    attendanceData: [{
      target_count: string,
      undistributed_count: string,
      confirmation_true_count: string,
      confirmation_false_count: string,
      attendance_button_name: string,
      count: string
    }],
    replyButtonNames: string[],
    setTargetNum: React.Dispatch<React.SetStateAction<number | null>>,
    setUndistributedNum: React.Dispatch<React.SetStateAction<number>>,
    setConfirmationNum: React.Dispatch<React.SetStateAction<number>>,
    setUnconfirmationNum: React.Dispatch<React.SetStateAction<number>>,
    setAttendanceButtonCount: React.Dispatch<React.SetStateAction<Map<string, number>>>,
    setUnattendanceNum: React.Dispatch<React.SetStateAction<number>>
  ) => {
    let targetNum = 0;
    let undistributedNum = 0;
    let confirmationNum = 0;
    let unconfirmationNum = 0;

    // 出欠状況集計
    attendanceData.forEach((data) => {
      targetNum += parseInt(data.target_count);
      undistributedNum += parseInt(data.undistributed_count);
      confirmationNum += parseInt(data.confirmation_true_count);
      unconfirmationNum += parseInt(data.confirmation_false_count);
    })
    setTargetNum(targetNum);
    setUndistributedNum(undistributedNum);
    setConfirmationNum(confirmationNum);
    setUnconfirmationNum(unconfirmationNum);

    // ボタン集計
    const replyButtonCount = new Map();
    replyButtonNames.forEach((name) => {
      const count = parseInt(attendanceData.find((rep) => rep.attendance_button_name === name)?.count ?? '0');
      replyButtonCount.set(name, count);
    });
    setAttendanceButtonCount(replyButtonCount);

    // 未回答数
    const unattendanceNum = parseInt(attendanceData.find((rep) => rep.attendance_button_name === '未回答')?.count ?? '0');
    setUnattendanceNum(unattendanceNum);
  };

  /// 出欠一覧データ作成
  attendanceData = (
    selectMessage: Messages,
    replyButtonSelected: ReplyButton | undefined,
    attendanceDataList: MessageAttendanceList[],
    buttonChange: boolean,
    checkChange: boolean,
    handleClickOpen: (item: MessageAttendanceList) => void,
    setMessageAttendanceList: React.Dispatch<React.SetStateAction<MessageAttendanceList[]>>,
    setButtonChange: React.Dispatch<React.SetStateAction<boolean>>,
    setCheckChange: React.Dispatch<React.SetStateAction<boolean>>,
    setChangeAttendanceData: React.Dispatch<React.SetStateAction<MessageAttendanceList | null>>,
    setNewAttendanceList: React.Dispatch<React.SetStateAction<MessageAttendanceList[]>>
  ) => {
      // 出欠結果ボタン押下イベント
      const onReplyButton = async (userId: string, buttonName: string, confirmed: boolean) => {
        await updateReadMessages(userId, { attendanceButtonName: buttonName, attendanceAt: new Date(), attendanceFlg: true, }, confirmed);
        setButtonChange(!buttonChange);
      };

      // チェックボックス押下イベント
      const onCheckBox = async (userId: string, isChecked: boolean, confirmed: boolean) => {
        await updateReadMessages(userId, { confirmationCheck: !isChecked }, confirmed);
        setCheckChange(!checkChange);
      };

      // 取消ボタン押下イベント
      const onCancel = (item: MessageAttendanceList) => {
        handleClickOpen(item);
      };

      // readMessages更新＆出欠一覧リスト更新処理
      const updateReadMessages = async (userId: string, updates: Partial<ReadMessages>, confirmed: boolean) => {
        const targetListItemIndex = attendanceDataList.findIndex((item) => item.userId === userId);
        if (targetListItemIndex > -1) {
          let readMessage = await userQuery.getTargetReadMessages(selectMessage.tenantId, userId, selectMessage.id);
          if (readMessage === null) {
            // 返信データが存在しない場合は追加
            readMessage = { messageId: selectMessage.id, userId, ...updates };
            await userQuery.updateReplyButton(selectMessage.tenantId, readMessage, confirmed, selectMessage);
          } else {
            // 返信データが存在する場合は更新
            if (readMessage.attendanceButtonName !== undefined && readMessage.attendanceButtonName === updates.attendanceButtonName) {
              // 同じボタンが押された場合、attendanceButtonName と attendanceAt を削除
              readMessage.attendanceButtonName = "未回答";
              await userQuery.clearAttendanceButton(selectMessage.tenantId, userId, confirmed, selectMessage);
            } else {
              readMessage = { ...readMessage, ...updates };
              await userQuery.updateReplyButton(selectMessage.tenantId, readMessage, confirmed, selectMessage);
            }
          }

          const oldAttendanceData = {...attendanceDataList[targetListItemIndex]};

          // 出欠一覧データ更新
          let newAttendanceDataList = [...attendanceDataList];
          newAttendanceDataList[targetListItemIndex].attendanceData = readMessage.attendanceButtonName ?? '未回答';
          newAttendanceDataList[targetListItemIndex].checkbox = readMessage.confirmationCheck ?? false;
          setMessageAttendanceList(newAttendanceDataList);

          const newAttendanceData = {...newAttendanceDataList[targetListItemIndex]};

          setChangeAttendanceData(oldAttendanceData);

          setNewAttendanceList(prev => {
            const cur = newAttendanceData;
            const next = prev.map(item => (item.userId === cur.userId ? cur : item));

            const exists = prev.some(item => item.userId === cur.userId);
            return exists ? next : [...next, cur];
          });
        }
      };

      return attendanceDataList.map(data => {
        // ボタンの幅を固定値に設定
        const maxButtonWidth = 72; // 6文字分の幅を固定（12px * 6文字）
        const attendanceState = replyButtonSelected && replyButtonSelected.buttonName ? replyButtonSelected.buttonName.map((item, index) => {
          let replyButtonColor = "bg-white text-blue-700";
          let additionalClasses = "border border-blue-700";

          let displayName = item;
          if (item === "会場で参加") {
            displayName = "会場";
          } else if (item === "オンラインで参加") {
            displayName = "オンライン";
          }

          if (item === data.attendanceData) {
            replyButtonColor = "bg-blue-700 text-white";
          } else {
            replyButtonColor = "bg-white text-blue-700";
          }

          if (selectMessage.municipalityConfirmedFlg) {
            replyButtonColor += " opacity-50";
            additionalClasses += " cursor-not-allowed";
          }

          return (
            <Badge
              key={`badge-${data.displayId}-${index}`}
              color="blue"
              className={`${replyButtonColor} ${additionalClasses} px-2 py-2 text-sm rounded-md w-full sm:w-auto break-words`}
              onClick={() => !selectMessage.municipalityConfirmedFlg && onReplyButton(data.userId, item, selectMessage.staffConfirmedFlg ?? false)}
            >
              <div style={{ width: `${maxButtonWidth}px`, textAlign: 'center', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{displayName}</div>
            </Badge>
          );
        }) : (<></>);

        return {
          rowData: {
            userId: data.displayId,
            checkbox: <CheckBox id={data.displayId} checked={data.checkbox} onChange={() => onCheckBox(data.userId, data.checkbox, selectMessage.staffConfirmedFlg ?? false)} className={selectMessage.municipalityConfirmedFlg ? "opacity-50 cursor-not-allowed" : ""} disabled={selectMessage.municipalityConfirmedFlg} />,
            status: data.status ? <span className={"text-blue-700 font-bold"}>配信外</span> : (<></>),
            readed: data.readed === null ? (<></>) : (data.readed ? <span className={"text-green-700 font-bold"}>既読</span> : <span className={"text-red-700 font-bold"}>未読</span>),
            name: `${data.lastName}`,
            account: data.account === '利用可' ? <span className={"text-green-700 font-bold"}>{data.account}</span> : <span className={"text-red-700 font-bold"}>{data.account}</span>,
            attendance: (<>{attendanceState}</>),
            cancel: data.status ? (
              <Button
                onClick={() => onCancel(data)}
                color="red"
                outline={true}
                className={`px-4 py-2 bg-white ${selectMessage.municipalityConfirmedFlg ? "opacity-50 cursor-not-allowed" : ""}`}
                disabled={selectMessage.municipalityConfirmedFlg}
              >
                取消
              </Button>
            ) : null,
            attendanceData: data.attendanceData,
            group: data.groups,
          },
          style: `sm:whitespace-normal`
        };
      });
  };

  // 配信先一覧のカラム
  deliveryGridCol: GridCol[] = [
    {
      field: "userId",
      headerName: "利用者番号",
      style: "w-1/4",
      sort: false
    },
    {
      field: "name",
      headerName: "名前",
      style: "w-1/4",
      sort: false
    },
    {
      field: "readed",
      headerName: "既読状態",
      style: "w-1/4",
      sort: false
    },
    {
      field: "account",
      headerName: "アプリ利用",
      style: "w-1/4",
      sort: false
    },
  ];
  // 返信一覧のカラム
  replyGridCol: GridCol[] = [
    {
      field: "userId",
      headerName: "利用者番号",
      style: "w-1/6",
      sort: false
    },
    {
      field: "name",
      headerName: "名前",
      style: "w-1/6",
      sort: false
    },
    {
      field: "readed",
      headerName: "既読状態",
      style: "w-1/6",
      sort: false
    },
    {
      field: "account",
      headerName: "アプリ利用",
      style: "w-1/6",
      sort: false
    },
    {
      field: "reply",
      headerName: "利用者の回答",
      style: "w-2/6",
      sort: false
    },
  ];

  // 出欠一覧のカラム
  attendanceGridCol: GridCol[] = [
    {
      field: "checkbox",
      headerName: "",
      style: "w-1/12",
      sort: false
    },
    {
      field: "userId",
      headerName: "利用者番号",
      style: "w-1/12",
      sort: false
    },
    {
      field: "name",
      headerName: "名前",
      style: "w-2/12",
      sort: false
    },
    {
      field: "readed",
      headerName: "既読状態",
      style: "w-1/12",
      sort: false
    },
    {
      field: "status",
      headerName: "ステータス",
      style: "w-1/12",
      sort: false
    },
    {
      field: "account",
      headerName: "アプリ利用",
      style: "w-1/12",
      sort: false
    },
    {
      field: "attendance",
      headerName: "出欠結果",
      style: "w-3/12",
      sort: false
    },
    {
      field: "cancel",
      headerName: "",
      style: "w-1/12",
      sort: false
    },
  ];
}

//TimeStampからDateTimeに変換
const ConvertDate = (data: any | undefined) => {
  data = data !== null
    ? new Date(data.seconds * 1000)
    : data;
  return data;
}